// Global Biku chat state. One conversation per signed-in user, shared by every
// surface (navbar panel, onboarding side panel, wizard launcher) and saved to
// localStorage under a per-user key so it survives navigation and refresh.
// Pattern: src/composables/useSavedViewsDrawer.ts (module-level reactive state).
//
// Sends run here, not in a component, so a reply still lands if the panel that
// sent it is closed or unmounted mid-request. The router is not imported: it
// would be circular (routes -> DashboardLayout -> ChatAssistant -> here), so
// sendBikuMessage returns a status and the calling component redirects.

import { reactive } from 'vue'
import {
  postChatMessage,
  postChatFeedback,
  currentGroupId,
  rateLimitCode,
  type ChatHistoryEntry,
  type RateLimitCode,
} from '@/util/bikuChatApi'

export const MAX_STORED_MESSAGES = 100
const MAX_HISTORY = 20

export interface BikuMessage {
  role: 'user' | 'assistant'
  content: string
  messageId: number | null
  reportable: boolean
  reported: boolean
  reporting: boolean
}

export interface BikuContext {
  routePath: string
  taskId?: number | string | null
  categoryId?: number | string | null
}

const state = reactive({
  isOpen: false,
  messages: [] as BikuMessage[],
  conversationHistory: [] as ChatHistoryEntry[],
  conversationId: null as number | null,
  isLoading: false,
  rateLimit: null as RateLimitCode | null,
  ownerKey: null as string | null,
})

// Bumped by New chat and user switches so a reply for an old conversation is dropped.
let generation = 0

export function storageKeyForCurrentUser(): string | null {
  try {
    const companyId = localStorage.getItem('companyId')
    const profile = JSON.parse(localStorage.getItem('profile') || 'null')
    if (!companyId || !profile?.id) return null
    return `bikuChat:${companyId}:${profile.id}`
  } catch (e) {
    return null
  }
}

function clearState(): void {
  generation++
  state.messages = []
  state.conversationHistory = []
  state.conversationId = null
  state.isLoading = false
  state.rateLimit = null
}

// rateLimit and isOpen are deliberately not stored: a limit hit yesterday must
// not block today, and a refresh starts with the panel closed.
function persist(): void {
  if (!state.ownerKey) return
  try {
    const messages = state.messages.slice(-MAX_STORED_MESSAGES).map((m) => ({
      role: m.role,
      content: m.content,
      messageId: m.messageId,
      reportable: m.reportable,
      reported: m.reported,
    }))
    localStorage.setItem(state.ownerKey, JSON.stringify({
      v: 1,
      conversationId: state.conversationId,
      messages,
      conversationHistory: state.conversationHistory,
    }))
  } catch (e) {
    // Storage full or blocked: the chat still works for this page session.
  }
}

/** Load the current user's conversation if the signed-in user changed since last use. */
export function ensureOwner(): void {
  const key = storageKeyForCurrentUser()
  if (key === state.ownerKey) return
  clearState()
  state.ownerKey = key
  if (!key) return
  try {
    const stored = JSON.parse(localStorage.getItem(key) || 'null')
    if (stored && stored.v === 1) {
      state.conversationId = stored.conversationId ?? null
      state.conversationHistory = Array.isArray(stored.conversationHistory) ? stored.conversationHistory : []
      state.messages = (Array.isArray(stored.messages) ? stored.messages : []).map((m: any) => ({
        role: m.role,
        content: m.content,
        messageId: m.messageId ?? null,
        reportable: !!m.reportable,
        reported: !!m.reported,
        reporting: false,
      }))
    }
  } catch (e) {
    // Corrupt entry: start clean.
  }
}

export function useBikuChat() {
  ensureOwner()
  return state
}

export function openBikuChat(): void {
  ensureOwner()
  state.isOpen = true
}

export function closeBikuChat(): void {
  state.isOpen = false
}

export function toggleBikuChat(): void {
  ensureOwner()
  state.isOpen = !state.isOpen
}

export function startNewBikuChat(): void {
  clearState()
  persist()
}

/** Called on sign out. Clears memory only; logout already clears localStorage. */
export function resetBikuChat(): void {
  clearState()
  state.isOpen = false
  state.ownerKey = null
}

export type SendStatus = 'ok' | 'unauthorized' | 'rate_limited' | 'error' | 'ignored'

export async function sendBikuMessage(text: string, context: BikuContext): Promise<SendStatus> {
  ensureOwner()
  const trimmed = (text || '').trim()
  if (!trimmed || state.isLoading || state.rateLimit) return 'ignored'

  const myGeneration = generation
  // Flags must exist at push time to be reactive in Vue 2.
  state.messages.push({ role: 'user', content: trimmed, messageId: null, reportable: false, reported: false, reporting: false })
  state.isLoading = true
  persist()

  try {
    const data = await postChatMessage({
      message: trimmed,
      conversationHistory: state.conversationHistory,
      routePath: context.routePath,
      taskId: context.taskId ?? null,
      categoryId: context.categoryId ?? null,
      conversationId: state.conversationId,
      companyGroupId: currentGroupId(),
    })
    if (myGeneration !== generation) return 'ignored'
    state.conversationHistory = (data.conversationHistory || []).slice(-MAX_HISTORY)
    if (data.conversationId) state.conversationId = data.conversationId
    state.messages.push({
      role: 'assistant',
      content: data.reply,
      messageId: data.messageId ?? null,
      reportable: true,
      reported: false,
      reporting: false,
    })
    return 'ok'
  } catch (err: any) {
    if (myGeneration !== generation) return 'ignored'
    const limit = rateLimitCode(err)
    if (limit) {
      state.rateLimit = limit
      return 'rate_limited'
    }
    if (err?.response?.status === 401) return 'unauthorized'
    state.messages.push({
      role: 'assistant',
      content: 'Sorry, I encountered an error. Please try again.',
      messageId: null,
      reportable: false,
      reported: false,
      reporting: false,
    })
    return 'error'
  } finally {
    if (myGeneration === generation) {
      state.isLoading = false
      persist()
    }
  }
}

export type ReportStatus = 'ok' | 'limited' | 'error' | 'ignored'

export async function reportBikuMessage(
  index: number,
  rating: 'wrong_answer' | 'not_helpful',
  context: BikuContext,
): Promise<ReportStatus> {
  const msg = state.messages[index]
  if (!msg || msg.reporting || msg.reported) return 'ignored'
  msg.reporting = true

  // The user message that preceded the reported reply.
  let question = ''
  for (let j = index - 1; j >= 0; j--) {
    if (state.messages[j].role === 'user') {
      question = state.messages[j].content
      break
    }
  }

  try {
    await postChatFeedback({
      rating,
      flaggedAnswer: msg.content,
      question,
      conversationHistory: state.conversationHistory,
      routePath: context.routePath,
      taskId: context.taskId ?? null,
      companyGroupId: currentGroupId(),
      messageId: msg.messageId,
    })
    msg.reported = true
    persist()
    return 'ok'
  } catch (err: any) {
    return err?.response?.status === 429 ? 'limited' : 'error'
  } finally {
    msg.reporting = false
  }
}
